/* global api */
import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from '../plugins/persistent'
import authentication from './modules/authentication'
import forms from './modules/forms'
import branchUsers from './modules/branch-users'
import offline from './modules/offline'
import support from './modules/support'
import { FETCH_USER_SUCCESS } from './modules/authentication-mutation-types'
import { RESIDENT } from './modules/branch-user-types'
import { HEALTH_CHECK } from './modules/screening-types'
import { BranchUser, User, LogEntry } from '../models'
import localforage from 'localforage'
import Axios from 'axios'

Vue.use(Vuex)

const plugins = [
  createPersistedState({
    modules: [
      'offline',
      'support'
    ]
  })
]

function kioskCredentials () {
  const cred = {
    kioskUsername: '',
    kioskPassword: '',
    kioskEnabled: false
  }
  if (process.env.VUE_APP_ENV !== 'dev') {
    console.log('Trying to load prod stuff')
    Axios.get('/index.php?option=get_credentials').then((response) => {
      const data = response.data
      store.state.kioskUsername = data.a
      store.state.kioskPassword = data.b
      store.state.kioskEnabled = data.a !== undefined
    })
  } else {
    cred.kioskUsername = process.env.VUE_APP_GUEST_LOGIN
    cred.kioskPassword = process.env.VUE_APP_GUEST_PASSWORD
    cred.kioskEnabled = cred.kioskUsername !== undefined
  }
  console.log(cred)
  // Check Headers
  return cred
}

const store = new Vuex.Store({
  plugins: [...plugins],
  state: {
    token: process.env.VUE_APP_API_TOKEN,
    apiUrl: process.env.VUE_APP_API_URL,
    hotspotsUrl: process.env.VUE_APP_API_HOTSPOTS_URL,
    applicationReady: false,
    outstandingRequests: 0,
    overlayMessage: '',
    showOverlay: false,
    idleVue: {
      isIdle: false
    },
    selectedUser: {},
    lastScreening: {},
    lastScreeningForm: {},
    vaccineTracking: {},
    vaccineType: null,
    lastHealthCheckScreening: {},
    visitorInformation: {},
    pageAlertInformation: {
      variant: '',
      message: '',
      alerts: []
    },
    showInit: false,
    initSteps: 3,
    initProgress: 0,
    initFailure: false,
    initRetryLimit: 4,
    currentInitRetryAttempt: 0,
    showAbout: false,
    isSelf: false,
    isSelfSignInLogin: false,
    isBfmLogin: false,
    fetchedBranch: false,
    ...kioskCredentials(),
    branches: getBranches(),
    branch: 0,
    env: process.env.VUE_APP_ENV || 'Prod',
    newVisitors: []
  },
  mutations: {
    commitTurnOnKioskMode (state) {
      state.isSelf = true
    },
    commitActivateSelfSignInLogin (state) {
      state.isSelfSignInLogin = true
    },
    commitActivateBfmLogin (state) {
      state.isBfmLogin = true
    },
    commitDeactivateSelfSignInLogin (state) {
      state.isSelfSignInLogin = false
    },
    commitDeactivateBfmLogin (state) {
      state.isBfmLogin = false
    },
    commitApplicationReady (state, meta) {
      state.applicationReady = meta
    },
    commitOverlayMessage (state, meta) {
      state.overlayMessage = meta
    },
    commitOutstandingRequests (state, meta) {
      state.outstandingRequests = meta
    },
    commitShowOverlay (state, meta) {
      state.showOverlay = meta
    },
    commitSelectedUser (state, meta) {
      state.selectedUser = meta
      window.sessionStorage.setItem('selectedUser', JSON.stringify(meta))
    },
    commitPageAlertInformation (state, meta) {
      state.pageAlertInformation = meta
    },
    commitVisitorInformation (state, meta) {
      state.visitorInformation = meta
    },
    commitLastScreening (state, meta) {
      state.lastScreening = meta
    },
    commitLastScreeningForm (state, meta) {
      state.lastScreeningForm = meta
    },
    commitVaccineTracking (state, meta) {
      state.vaccineTracking = meta
    },
    commitVaccineType (state, meta) {
      state.vaccineType = meta
    },
    commitHealthCheckScreening (state, meta) {
      state.lastHealthCheckScreening = meta
    },
    commitShowInit (state, meta) {
      state.showInit = meta
    },
    commitInitProgress (state, meta) {
      state.initProgress = meta
    },
    commitInitStepComplete (state) {
      state.initProgress = state.initProgress + 1
    },
    commitInitFailure (state, meta) {
      state.initFailure = meta
    },
    commitPerformedInitRetryAttempt (state) {
      state.currentInitRetryAttempt = state.currentInitRetryAttempt + 1
    },
    commitResetInitRetryAttempt (state) {
      state.currentInitRetryAttempt = 0
    },
    commitShowAbout (state, meta) {
      state.showAbout = meta
    },
    commitSetFetchedBranch (state, meta) {
      state.fetchedBranch = meta
    },
    commitRemoveNewVisitor (state, uuid) {
      const filteredVisitors = state.newVisitors.filter(a => a.visitor.id !== uuid)
      console.log('Filtered Visitors')
      console.log(filteredVisitors)
      state.newVisitors = filteredVisitors
    },
    commitNewVisitors (state, meta) {
      state.newVisitors = meta
    },
    commitAddNewVisitor (state, meta) {
      console.log('Adding New Visitor')
      state.newVisitors.push(meta)
    },
    commitAddVisitorToBFMs (state, meta) {
      const branchUser = new BranchUser(meta)
      console.log('Branch User: ')
      console.log(branchUser)
      state.branchUsers.allBfmsWithBranchUsers.push(branchUser)
    }
  },
  actions: {
    async initialiseStore ({ commit }) {
      if (window.sessionStorage.getItem('selectedUser')) {
        const selectedUser = await JSON.parse(window.sessionStorage.getItem('selectedUser'))
        await commit('commitSelectedUser', selectedUser)
      }
    },
    activateKioskMode ({ commit }) {
      console.log('Activating Kiosk Mode')
      commit('commitTurnOnKioskMode')
    },
    activateSelfSignInLogin ({ commit }) {
      commit('commitActivateSelfSignInLogin')
    },
    activateBfmLogin ({ commit }) {
      commit('commitActivateBfmLogin')
    },
    backToLoginSelect ({ commit }) {
      commit('commitDeactivateBfmLogin')
      commit('commitDeactivateSelfSignInLogin')
    },
    setApplicationReady ({ commit }, meta) {
      commit('commitApplicationReady', meta)
    },
    setLoading ({ commit, state }, meta) {
      if (meta === true) {
        commit('commitOutstandingRequests', state.outstandingRequests + 1)
        commit('commitShowOverlay', true)
      } else {
        commit('commitOutstandingRequests', state.outstandingRequests - 1)
        commit('commitOverlayMessage', '')
        commit('commitShowOverlay', false)
      }
    },
    setOverlayMessage ({ commit }, meta) {
      commit('commitOverlayMessage', meta)
    },
    setShowOverlay ({ commit }, meta) {
      commit('commitShowOverlay', meta)
    },
    async setSelectedUser ({ commit, state, getters, dispatch }, meta) {
      if (meta.itemId === 0 && meta.type === 'new') {
        console.log('NEW VISITOR SELECTED!')
        if (meta.visitor) {
          dispatch('setVisitorInformation', meta.visitor)
        }
        return
      }
      if (meta.itemId && navigator.onLine) {
        const branchUser = new BranchUser(meta)
        const method = 'post'
        let result = await api.call(method, state.apiUrl + 'getLastScreening', branchUser.visitorData)
        if (result.success) {
          console.log('API Call for GET LAST SCREENING')
          console.log(result)
          const ls = result.message.lastScreening
          const vTracking = result.message.vaccineTracking
          const vType = result.message.vaccineType
          commit('commitLastScreening', ls)
          commit('commitVaccineTracking', vTracking)
          commit('commitVaccineType', vType)
          if (ls !== null && ls.form_id !== undefined) {
            let lastScreening = state.forms.questionnaires.find(questionnaire => questionnaire.id === ls.form_id)
            if (lastScreening === undefined) {
              lastScreening = {}
            }
            commit('commitLastScreeningForm', lastScreening)

            if (branchUser.type === RESIDENT) {
              if (lastScreening.route === HEALTH_CHECK) {
                commit('commitHealthCheckScreening', ls)
              } else {
                const healthCheckForm = getters['forms/findQuestionnaireByRoute'](HEALTH_CHECK)
                if (healthCheckForm.id !== undefined) {
                  const visitorData = branchUser.visitorData
                  visitorData.form_id = healthCheckForm.id
                  result = await api.call(method, state.apiUrl + 'getLastScreeningOfType', visitorData)
                  if (result.success && ls !== null) {
                    commit('commitHealthCheckScreening', result.message)
                  }
                }
              }
            }
          }
        }
      }
      commit('commitSelectedUser', meta)
    },
    clearSelectedUser ({ commit }) {
      commit('commitSelectedUser', {})
    },
    setPageAlertInformation ({ commit, getters }, meta) {
      if (!getters.showInitProgress) {
        commit('commitPageAlertInformation', meta)
      }
    },
    clearPageAlertInformation ({ commit }) {
      const defaultInformation = {
        variant: '',
        message: ''
      }
      commit('commitPageAlertInformation', defaultInformation)
    },
    setVisitorInformation ({ commit }, meta) {
      const data = {}
      data.item_name = meta.firstName + ' ' + meta.lastName
      data.search_data = data.item_name + ' - Visitor'
      data.type = 'visitors'
      data.visitorType = meta.visitorType
      const branchUser = new BranchUser(data)
      commit('commitVisitorInformation', meta)
      commit('commitSelectedUser', branchUser)
    },
    setVisitorTypeInformation ({ commit }, meta) {
      const visitor = {
        user_id: meta.itemId,
        visitorType: meta.visitorType
      }
      console.log('V Type Info')
      console.log(meta)
      commit('commitVisitorInformation', visitor)
    },
    async clearLastScreening ({ commit, state }) {
      if (Object.keys(state.visitorInformation).length !== 0 && state.visitorInformation.constructor === Object) {
        commit('commitVisitorInformation', {})
        const user = new User(state.authentication.user)
        if (user.hasPrimaryBranchId) {
          const branchUserObj = {
            branch_id: user.primaryBranchId,
            user_id: user.id
          }
          if (state.isSelf && state.fetchedBranch) {
            await store.dispatch('branchUsers/fetchBranchUsers', branchUserObj)
          }
          if (!state.isSelf) {
            await store.dispatch('branchUsers/fetchBranchUsers', branchUserObj)
          }
          commit('commitSetFetchedBranch', true)
        }
      }
      commit('commitLastScreening', {})
      commit('commitLastScreeningForm', {})
      commit('commitHealthCheckScreening', {})
    },
    setShowInit ({ commit }, init) {
      commit('commitShowInit', init)
    },
    setInitProgress ({ commit }, meta) {
      commit('commitInitProgress', meta)
    },
    async setInitStepComplete ({ commit, dispatch, state }, meta) {
      const initStepCompleteLogEntry = new LogEntry('index/setInitStepComplete', 'Step: ' + meta)
      dispatch('support/addLog', initStepCompleteLogEntry)

      const currentProgressLogEntry = new LogEntry('index/setInitStepComplete', 'Current Progress: ' + state.initProgress)
      dispatch('support/addLog', currentProgressLogEntry)

      await commit('commitInitStepComplete')

      const newProgressLogEntry = new LogEntry('index/setInitStepComplete', 'New Progress: ' + state.initProgress)
      dispatch('support/addLog', newProgressLogEntry)

      if (state.initProgress >= state.initSteps) {
        await commit('commitShowInit', false)
        await commit('commitApplicationReady', true)

        const logEntry = new LogEntry('index/setInitStepComplete', 'applicationReady: true')
        await dispatch('support/addLog', logEntry)
      }
    },
    setInitFailure ({ commit }, meta) {
      commit('commitInitFailure', meta)
    },
    performedInitRetryAttempt ({ commit }) {
      commit('commitPerformedInitRetryAttempt')
    },
    resetInitRetryAttempt ({ commit }) {
      commit('commitResetInitRetryAttempt')
    },
    setShowAboutModal ({ commit }, meta) {
      commit('commitShowAbout', meta)
    },
    async addNewVisitor ({ commit, state, dispatch }, meta) {
      commit('commitAddNewVisitor', meta)
      await localforage.setItem('firstTimeVisitorCache', state.newVisitors)
      console.log('Commited new Visitors')
      console.log(state.newVisitors)
    },
    async removeNewVisitor ({ state, dispatch, commit }, meta) {
      await commit('commitRemoveNewVisitor', meta)
      await dispatch('commitNewVisitorsToCache', state.newVisitors)
    },
    async getNewVisitorsFromCache ({ commit, state }) {
      const newVisitors = await localforage.getItem('firstTimeVisitorCache', state.newVisitors)
      if (newVisitors) {
        commit('commitNewVisitors', newVisitors)
      }
    },
    async commitNewVisitorsToCache ({ commit, state }) {
      await localforage.setItem('firstTimeVisitorCache', state.newVisitors)
    },
    addVisitorToBFMs ({ commit }, meta) {
      commit('commitAddVisitorToBFMs', meta)
    }
  },
  getters: {
    applicationReady: state => {
      return state.applicationReady
    },
    apiUrl: state => {
      return state.apiUrl
    },
    showOverlay: state => {
      return state.showOverlay
    },
    overlayMessage: state => {
      const overlayMessageTmp = state.overlayMessage
      return overlayMessageTmp
    },
    loading: state => {
      return state.outstandingRequests > 0
    },
    selectedUser: state => {
      return state.selectedUser
    },
    userSelected: state => {
      return !(Object.keys(state.selectedUser).length === 0 && state.selectedUser.constructor === Object)
    },
    pageAlertInformation: state => {
      const pageAlertInformationTmp = state.pageAlertInformation
      return pageAlertInformationTmp
    },
    visitorInformation: state => {
      return state.visitorInformation
    },
    isSelectedUserTypeOf: (state) => (branchUserType) => {
      return state.selectedUser.type === branchUserType
    },
    lastScreening: state => {
      return state.lastScreening
    },
    lastScreeningForm: state => {
      return state.lastScreeningForm
    },
    vaccineTracking: state => {
      return state.vaccineTracking
    },
    vaccineType: state => {
      return state.vaccineType
    },
    lastHealthCheckScreening: state => {
      return state.lastHealthCheckScreening
    },
    showInit: state => state.showInit,
    initSteps: state => state.initSteps,
    initProgress: state => state.initProgress,
    initFailure: state => state.initFailure,
    initRetryLimit: state => state.initRetryLimit,
    currentInitRetryAttempt: state => state.currentInitRetryAttempt,
    showInitProgress: state => {
      if (state.showInit && (state.initProgress < state.initSteps)) {
        return true
      }
      return false
    },
    showAbout: state => state.showAbout,
    kioskEnabled: state => state.kioskEnabled,
    newVisitors: state => state.newVisitors
  },
  modules: {
    authentication,
    forms,
    branchUsers,
    offline,
    support
  }
})

// function getBetseCache () {
//   const db = window.indexedDB.open('betse_cache', 1)
//   const transaction = db.transaction('cachedBFMS', )
//
// }

store.subscribe(async (mutation, state) => {
  if (mutation.type === 'authentication/' + FETCH_USER_SUCCESS) {
    const user = new User(state.authentication.user)
    if (user.hasPrimaryBranchId) {
      const hotspotParams = []
      hotspotParams.push(user.primaryBranchId)
      hotspotParams.push(user.state)
      const branchUserObj = {
        branch_id: user.primaryBranchId,
        user_id: user.id
      }

      const startLoginEntry = new LogEntry('index/subscribe', 'Start: User login')
      await store.dispatch('support/addLog', startLoginEntry)

      await store.dispatch('forms/fetchQuestionaires', branchUserObj)
      await store.dispatch('forms/fetchHotspots', hotspotParams)
      if (state.isSelf) {
        const cachedBFMS = await localforage.getItem('cached_bfms')
        const cachedBranch = await localforage.getItem('cached_branch')
        if (cachedBFMS) {
          const branchId = store.state.branch
          console.log('BRANCH ID cached: ' + cachedBranch + ' current: ' + branchId)
          if (branchId == cachedBranch) {
            await store.commit('branchUsers/commitAllBfmsWithBranchUsers', cachedBFMS)
            await store.commit('authentication/commitSetSimulatingUser', true)
          } else {
            if (store.state.authentication.simulatingUser) {
              console.log('Mismatched branch fetching Branch Users')
              await localforage.setItem('cached_branch', store.state.branch)
              await store.dispatch('setOverlayMessage', 'Loading...')
              await store.dispatch('setLoading', true)
              await store.dispatch('branchUsers/fetchBranchUsers', branchUserObj)
              await store.dispatch('setLoading', false)
            } else {
              await store.commit('authentication/commitSetSimulatingUser', true)
            }
          }
        } else if (store.state.authentication.simulatingUser) {
          await store.dispatch('setOverlayMessage', 'Loading...')
          await store.dispatch('setLoading', true)
          await store.dispatch('branchUsers/fetchBranchUsers', branchUserObj)
          await store.dispatch('setLoading', false)
        } else {
          await store.commit('authentication/commitSetSimulatingUser', true)
        }
        const cachedNewVisitors = await localforage.getItem('firstTimeVisitorCache')
        if (cachedNewVisitors) {
          await store.dispatch('getNewVisitorsFromCache', cachedNewVisitors)
        }
      } else {
        await store.dispatch('branchUsers/fetchBranchUsers', branchUserObj)
      }

      const endLoginEntry = new LogEntry('index/subscribe', 'End: User login')
      await store.dispatch('support/addLog', endLoginEntry)
    }
  }
})

export default store
function getBranches () {
  return [
    { value: null, text: 'Please select a branch' },
    { value: 0, text: 'Branch Support' },
    { value: 8, text: 'Ames' },
    { value: 54, text: 'Aurora' },
    { value: 9, text: 'Battle Creek' },
    { value: 63, text: 'Bexley' },
    { value: 2, text: 'Bloomington' },
    { value: 10, text: 'Bourbonnais' },
    { value: 12, text: 'Burlington' },
    { value: 69, text: 'Canton' },
    { value: 46, text: 'Carmel' },
    { value: 5, text: 'Cedar Falls' },
    { value: 13, text: 'Champaign' },
    { value: 70, text: 'Chesapeake' },
    { value: 51, text: 'Chesterfield' },
    { value: 14, text: 'Clinton' },
    { value: 15, text: 'Crawfordsville' },
    { value: 44, text: 'Crown Point' },
    { value: 16, text: 'Crystal Lake' },
    { value: 17, text: 'Davenport' },
    { value: 18, text: 'Fort Dodge' },
    { value: 19, text: 'Grand Island' },
    { value: 45, text: 'Greenwood' },
    { value: 56, text: 'Gurnee' },
    { value: 20, text: 'Iowa City' },
    { value: 21, text: 'Lafayette' },
    { value: 50, text: 'Lancaster' },
    { value: 22, text: 'Lincoln' },
    { value: 23, text: 'Macomb' },
    { value: 25, text: 'Marion' },
    { value: 26, text: 'Marshalltown' },
    { value: 49, text: 'Middletown' },
    { value: 3, text: 'Midland' },
    { value: 27, text: 'Mission Springs' },
    { value: 1, text: 'Moline' },
    { value: 28, text: 'Muscatine' },
    { value: 29, text: 'Okemos' },
    { value: 30, text: 'Omaha - Blondo' },
    { value: 31, text: 'Omaha - Hickory' },
    { value: 32, text: 'Oswego' },
    { value: 33, text: 'Overland Park' },
    { value: 6, text: 'Peoria - House' },
    { value: 47, text: 'Peoria - Mary B\'s' },
    { value: 34, text: 'Portage' },
    { value: 67, text: 'Presque Isle Bay' },
    { value: 35, text: 'Quincy' },
    { value: 36, text: 'Raytown' },
    { value: 37, text: 'Rockford' },
    { value: 66, text: 'Rocky River' },
    { value: 4, text: 'Saginaw Township' },
    { value: 64, text: 'Scioto' },
    { value: 58, text: 'Shelby Township' },
    { value: 38, text: 'Sioux City - Cottage' },
    { value: 48, text: 'Sioux City - Mary B\'s' },
    { value: 52, text: 'Spotsylvania' },
    { value: 39, text: 'Springfield' },
    { value: 40, text: 'St Charles' },
    { value: 55, text: 'Suffolk' },
    { value: 53, text: 'Tinley Park' },
    { value: 41, text: 'Urbandale' },
    { value: 68, text: 'Virginia Beach' },
    { value: 42, text: 'Wabash' },
    { value: 43, text: 'West Des Moines' },
    { value: 57, text: 'West Lansing' },
    { value: 65, text: 'Worthington' }
  ]
}
